import React, { useCallback, useEffect, useState } from 'react';
import { DiscardChangesConfirmationActionSheet } from '@/components/discard-changes-confirmation-action-sheet';

interface Params {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onClose: () => void;
  onRegisterCloseHandler?: (handler: () => void) => void;
  save: () => Promise<void>;
}

/**
 * Shared unsaved-changes flow: register a close handler with the parent shell,
 * and render the standard save/discard confirmation action sheet.
 */
export const useDiscardChangesOnClose = ({
  hasUnsavedChanges,
  isSaving,
  onClose,
  onRegisterCloseHandler,
  save,
}: Params) => {
  const [sheetVisible, setSheetVisible] = useState(false);

  const handleAttemptClose = useCallback(() => {
    if (isSaving) {
      return;
    }
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }
    setSheetVisible(true);
  }, [hasUnsavedChanges, isSaving, onClose]);

  useEffect(() => {
    onRegisterCloseHandler?.(handleAttemptClose);
  }, [handleAttemptClose, onRegisterCloseHandler]);

  const dismissSheet = () => {
    setSheetVisible(false);
  };

  const discardAndClose = () => {
    setSheetVisible(false);
    onClose();
  };

  const discardConfirmationSheet = (
    <DiscardChangesConfirmationActionSheet
      isSaving={isSaving}
      isVisible={sheetVisible}
      onDiscard={discardAndClose}
      onDismiss={dismissSheet}
      onSaveFromSheet={save}
    />
  );

  return { discardConfirmationSheet };
};
